import { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { useComfort } from "../ComfortContext";
import { getDictionary } from "../../i18n/dictionary";
import {
  CONSENT_SCOPES,
  grant as grantScope,
  revoke as revokeScope,
  revokeAll as revokeAllScopes,
} from "../../domain/consent";
import type { ConsentScopeKey } from "../../domain/types";
import {
  FIXTURE_ACCOUNT_ID,
  getLinkedChildren,
  getOwnedChildrenWithViewers,
  buildConsentState,
  type ConsentState,
  type LinkState,
} from "./data";
import { getPortalData, shouldUseSupabase } from "../../data";
import type { ServiceOutletContext } from "./ServiceSettings";

/** Opaque, PII-free reference for an adult whose email RLS withholds. */
export function linkRef(accountId: string): string {
  return `Linked adult ··${accountId.slice(-4)}`;
}
/** Opaque, PII-free reference for a child (the name lives on-device only). */
export function childRef(childId: string): string {
  return `Child ··${childId.slice(-4)}`;
}

function scopeLabel(
  tc: ReturnType<typeof getDictionary>["service"]["consent"],
  scope: ConsentScopeKey,
): string {
  if (scope === "progress") return tc.scopeProgress;
  if (scope === "results") return tc.scopeResults;
  return tc.scopeExport;
}

// ---- Scope badge (viewer read-only) ------------------------------------------

function ScopeBadge({ label }: { label: string }) {
  return (
    <span
      style={{
        display: "inline-block",
        padding: "3px 10px",
        borderRadius: "var(--r-pill)",
        background: "var(--brand-tint)",
        color: "var(--brand)",
        fontSize: 12.5,
        fontWeight: 600,
        letterSpacing: ".04em",
      }}
    >
      {label}
    </span>
  );
}

// ---- Revoke confirm modal -----------------------------------------------------

function RevokeConfirmModal({
  email,
  note,
  cancelLabel,
  confirmLabel,
  onCancel,
  onConfirm,
}: {
  email: string;
  note: string;
  cancelLabel: string;
  confirmLabel: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="revoke-modal-title"
      style={{
        position: "fixed",
        inset: 0,
        display: "grid",
        placeItems: "center",
        background: "rgba(0,0,0,.35)",
        zIndex: 1000,
        padding: 20,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        style={{
          background: "var(--surface)",
          borderRadius: "var(--r-xl, 20px)",
          padding: "28px 24px",
          width: "100%",
          maxWidth: 440,
          boxShadow: "0 8px 32px rgba(0,0,0,.18)",
        }}
      >
        <h2
          id="revoke-modal-title"
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 700,
            fontSize: 20,
            color: "var(--ink-deep)",
            margin: "0 0 10px",
          }}
        >
          Remove all access?
        </h2>
        <p
          style={{
            fontSize: 15,
            color: "var(--ink-soft)",
            lineHeight: 1.6,
            margin: "0 0 4px",
          }}
        >
          This will remove{" "}
          <strong style={{ color: "var(--ink)" }}>{email}</strong> from seeing
          this child's summaries.
        </p>
        <div
          style={{
            background: "var(--peach-tint)",
            border: "1px solid var(--peach)",
            borderRadius: "var(--r-lg)",
            padding: "13px 15px",
            margin: "14px 0 22px",
          }}
        >
          <p
            style={{
              fontSize: 13.5,
              color: "var(--peach-deep)",
              margin: 0,
              lineHeight: 1.55,
            }}
          >
            {note}
          </p>
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button
            onClick={onCancel}
            style={{
              minHeight: 44,
              padding: "0 20px",
              border: "1.5px solid var(--line)",
              background: "transparent",
              color: "var(--ink-soft)",
              fontFamily: "var(--font-body)",
              fontWeight: 600,
              fontSize: 14,
              borderRadius: "var(--r-pill)",
              cursor: "pointer",
            }}
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            style={{
              minHeight: 44,
              padding: "0 20px",
              border: 0,
              background: "var(--peach)",
              color: "var(--peach-deep)",
              fontFamily: "var(--font-body)",
              fontWeight: 700,
              fontSize: 14,
              borderRadius: "var(--r-pill)",
              cursor: "pointer",
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- Viewer card (owner sees one linked adult) --------------------------------

function ViewerCard({
  childId,
  accountId,
  email,
  effectiveLinkState,
  consentState,
  tc,
  onToggle,
  onRevokeAll,
}: {
  childId: string;
  accountId: string;
  email: string;
  effectiveLinkState: LinkState;
  consentState: ConsentState;
  tc: ReturnType<typeof getDictionary>["service"]["consent"];
  onToggle: (
    childId: string,
    accountId: string,
    scope: ConsentScopeKey,
    checked: boolean,
  ) => void;
  onRevokeAll: (childId: string, accountId: string, email: string) => void;
}) {
  const granted = consentState.grants[accountId] ?? [];
  const isRevoked = effectiveLinkState === "revoked";
  const isPending = effectiveLinkState === "pending";

  return (
    <div
      style={{
        background: isRevoked ? "var(--surface-sunk)" : "var(--surface)",
        border: `1px solid ${isRevoked ? "var(--line-strong)" : "var(--line)"}`,
        borderRadius: "var(--r-lg)",
        padding: "16px 20px",
        display: "flex",
        flexDirection: "column",
        gap: 4,
      }}
    >
      {/* Header row */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: 8,
        }}
      >
        <div>
          <span
            style={{
              fontWeight: 600,
              fontSize: 15,
              color: isRevoked ? "var(--ink-faint)" : "var(--ink-deep)",
              display: "block",
            }}
          >
            {email}
          </span>
          {isRevoked && (
            <span style={{ fontSize: 12.5, color: "var(--ink-faint)" }}>
              Access removed
            </span>
          )}
          {isPending && !isRevoked && (
            <span style={{ fontSize: 12.5, color: "var(--peach-deep)" }}>
              No scopes granted yet — toggle below to enable access
            </span>
          )}
        </div>
        {!isRevoked && (
          <button
            onClick={() => onRevokeAll(childId, accountId, email)}
            aria-label={`Remove all access for ${email}`}
            style={{
              minHeight: 36,
              padding: "0 14px",
              border: "1.5px solid var(--peach-deep)",
              background: "transparent",
              color: "var(--peach-deep)",
              fontFamily: "var(--font-body)",
              fontWeight: 600,
              fontSize: 13,
              borderRadius: "var(--r-pill)",
              cursor: "pointer",
              flex: "none",
            }}
          >
            {tc.revokeAll}
          </button>
        )}
      </div>

      {/* Scope toggles */}
      {!isRevoked && (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {CONSENT_SCOPES.map((scope) => {
            const id = `scope-${childId}-${accountId}-${scope}`;
            const label = scopeLabel(tc, scope);
            const checked = granted.includes(scope);
            return (
              <label
                key={scope}
                htmlFor={id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  cursor: "pointer",
                  userSelect: "none",
                  minHeight: 44,
                  fontSize: 14.5,
                  color: "var(--ink)",
                  borderRadius: "var(--r-sm)",
                  padding: "0 4px",
                }}
              >
                <input
                  id={id}
                  type="checkbox"
                  checked={checked}
                  onChange={(e) =>
                    onToggle(childId, accountId, scope, e.target.checked)
                  }
                  style={{
                    width: 18,
                    height: 18,
                    cursor: "pointer",
                    accentColor: "var(--brand)",
                    flex: "none",
                  }}
                />
                {label}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---- Section header ----------------------------------------------------------

function SectionHeader({ label }: { label: string }) {
  return (
    <h2
      style={{
        fontFamily: "var(--font-display)",
        fontWeight: 700,
        fontSize: 13,
        letterSpacing: ".12em",
        textTransform: "uppercase",
        color: "var(--ink-faint)",
        margin: "0 0 12px",
      }}
    >
      {label}
    </h2>
  );
}

// ---- Main export -------------------------------------------------------------

/** Dispatcher: real consent management when Supabase is configured, else the demo. */
export function SharingConsent() {
  return shouldUseSupabase() ? <SharingConsentLive /> : <SharingConsentDemo />;
}

function SharingConsentDemo() {
  const { locale } = useComfort();
  const dict = getDictionary(locale);
  const tc = dict.service.consent;
  const common = dict.common;
  const tch = dict.service.children;

  const ownedChildren = getOwnedChildrenWithViewers(FIXTURE_ACCOUNT_ID);
  const viewerLinked = getLinkedChildren(FIXTURE_ACCOUNT_ID).filter(
    (lc) => lc.link.relation === "viewer",
  );

  // Per-child consent state (owner-side, mutable within this session).
  const [consentStates, setConsentStates] = useState<
    Record<string, ConsentState>
  >(() =>
    Object.fromEntries(
      ownedChildren.map((o) => [o.child.id, buildConsentState(o.child.id)]),
    ),
  );

  // Viewers that had revokeAll applied this session (tracked for UI state).
  const [revokedViewers, setRevokedViewers] = useState<Set<string>>(
    new Set(),
  );

  // Which viewer is pending revoke-all confirmation.
  const [revokeTarget, setRevokeTarget] = useState<{
    childId: string;
    viewerAccountId: string;
    email: string;
  } | null>(null);

  function handleToggle(
    childId: string,
    viewerAccountId: string,
    scope: ConsentScopeKey,
    checked: boolean,
  ) {
    setConsentStates((prev) => {
      const state = prev[childId];
      if (!state) return prev;
      const next = checked
        ? grantScope(state, FIXTURE_ACCOUNT_ID, viewerAccountId, scope)
        : revokeScope(state, FIXTURE_ACCOUNT_ID, viewerAccountId, scope);
      return { ...prev, [childId]: next };
    });
  }

  function handleRevokeAll(
    childId: string,
    viewerAccountId: string,
    email: string,
  ) {
    setRevokeTarget({ childId, viewerAccountId, email });
  }

  function confirmRevokeAll() {
    if (!revokeTarget) return;
    setConsentStates((prev) => {
      const state = prev[revokeTarget.childId];
      if (!state) return prev;
      const { state: next } = revokeAllScopes(
        state,
        FIXTURE_ACCOUNT_ID,
        revokeTarget.viewerAccountId,
      );
      return { ...prev, [revokeTarget.childId]: next };
    });
    setRevokedViewers(
      (prev) =>
        new Set([...prev, `${revokeTarget.childId}:${revokeTarget.viewerAccountId}`]),
    );
    setRevokeTarget(null);
  }

  const hasOwnerContent = ownedChildren.length > 0;
  const hasViewerContent = viewerLinked.length > 0;

  return (
    <div>
      {/* Page header */}
      <h1
        style={{
          fontFamily: "var(--font-display)",
          fontWeight: 700,
          fontSize: 28,
          color: "var(--ink-deep)",
          margin: "0 0 8px",
        }}
      >
        {tc.title}
      </h1>
      <p
        style={{
          color: "var(--ink-soft)",
          fontSize: 15.5,
          margin: "0 0 28px",
          lineHeight: 1.6,
        }}
      >
        You control what others can see about any child you own. Revoking takes
        effect immediately.
      </p>

      {/* Empty state */}
      {!hasOwnerContent && !hasViewerContent && (
        <div
          style={{
            background: "var(--surface)",
            border: "1px solid var(--line)",
            borderRadius: "var(--r-lg)",
            padding: "24px 22px",
          }}
        >
          <p style={{ color: "var(--ink-soft)", fontSize: 15.5, margin: 0 }}>
            No sharing set up yet. Link a child to get started.
          </p>
        </div>
      )}

      {/* Owner section — manage who can see your children */}
      {hasOwnerContent && (
        <section style={{ marginBottom: hasViewerContent ? 40 : 0 }}>
          <SectionHeader label={tc.whatShared} />
          <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
            {ownedChildren.map(({ child, viewers }) => (
              <div key={child.id}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    marginBottom: 12,
                  }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      background: "var(--brand-tint)",
                      display: "grid",
                      placeItems: "center",
                      fontFamily: "var(--font-display)",
                      fontWeight: 700,
                      fontSize: 15,
                      color: "var(--brand)",
                      flex: "none",
                    }}
                  >
                    {child.displayLabel.charAt(0)}
                  </span>
                  <h3
                    style={{
                      fontFamily: "var(--font-display)",
                      fontWeight: 700,
                      fontSize: 18,
                      color: "var(--ink-deep)",
                      margin: 0,
                    }}
                  >
                    {child.displayLabel}
                  </h3>
                </div>

                {viewers.length === 0 ? (
                  <p
                    style={{
                      color: "var(--ink-soft)",
                      fontSize: 15,
                      background: "var(--surface)",
                      border: "1px solid var(--line)",
                      borderRadius: "var(--r-lg)",
                      padding: "16px 20px",
                      margin: 0,
                    }}
                  >
                    No adults are linked to this child yet.
                  </p>
                ) : (
                  <div
                    style={{ display: "flex", flexDirection: "column", gap: 10 }}
                  >
                    {viewers.map((v) => {
                      const state = consentStates[child.id];
                      if (!state) return null;
                      const key = `${child.id}:${v.accountId}`;
                      const effectiveLinkState: LinkState = revokedViewers.has(key)
                        ? "revoked"
                        : v.linkState;
                      return (
                        <ViewerCard
                          key={v.accountId}
                          childId={child.id}
                          accountId={v.accountId}
                          email={v.email}
                          effectiveLinkState={effectiveLinkState}
                          consentState={state}
                          tc={tc}
                          onToggle={handleToggle}
                          onRevokeAll={handleRevokeAll}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
          <p
            style={{
              fontSize: 13,
              color: "var(--ink-faint)",
              marginTop: 18,
              lineHeight: 1.55,
            }}
          >
            {tc.immediateNote}
          </p>
        </section>
      )}

      {/* Viewer section — children shared with you (read-only) */}
      {hasViewerContent && (
        <section>
          <SectionHeader label="Children shared with you" />
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {viewerLinked.map((lc) => {
              const { child, link, linkState, ownerEmail } = lc;
              const scopes = link.scope;
              const isActive = linkState === "active";
              const isPending = linkState === "pending";
              const isRevoked = linkState === "revoked";

              const bg = isRevoked
                ? "var(--surface-sunk)"
                : isPending
                  ? "var(--peach-tint)"
                  : "var(--surface)";
              const borderColor = isRevoked
                ? "var(--line-strong)"
                : isPending
                  ? "var(--peach)"
                  : "var(--line)";

              return (
                <div
                  key={child.id}
                  style={{
                    background: bg,
                    border: `1px solid ${borderColor}`,
                    borderRadius: "var(--r-lg)",
                    padding: "16px 20px",
                  }}
                >
                  {/* Child identity row */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      marginBottom:
                        isActive && scopes.length > 0 ? 12 : 0,
                    }}
                  >
                    <span
                      aria-hidden="true"
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: "50%",
                        background: isRevoked
                          ? "var(--surface-sunk)"
                          : "var(--brand-tint)",
                        display: "grid",
                        placeItems: "center",
                        fontFamily: "var(--font-display)",
                        fontWeight: 700,
                        fontSize: 15,
                        color: isRevoked
                          ? "var(--ink-faint)"
                          : "var(--brand)",
                        flex: "none",
                        border: "1px solid var(--line)",
                      }}
                    >
                      {child.displayLabel.charAt(0)}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <span
                        style={{
                          fontWeight: 600,
                          fontSize: 15,
                          color: isRevoked
                            ? "var(--ink-faint)"
                            : "var(--ink-deep)",
                          display: "block",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {child.displayLabel}
                      </span>
                      {ownerEmail && (
                        <span
                          style={{
                            fontSize: 13,
                            color: isRevoked
                              ? "var(--ink-faint)"
                              : "var(--ink-soft)",
                          }}
                        >
                          {tch.viewerTag} {ownerEmail}
                        </span>
                      )}
                    </div>
                    {isPending && (
                      <span
                        style={{
                          fontSize: 12.5,
                          color: "var(--peach-deep)",
                          fontWeight: 600,
                          flex: "none",
                        }}
                      >
                        {tch.pendingTag}
                      </span>
                    )}
                    {isRevoked && (
                      <span
                        style={{
                          fontSize: 12.5,
                          color: "var(--ink-faint)",
                          fontWeight: 600,
                          flex: "none",
                        }}
                      >
                        {tch.revokedTag}
                      </span>
                    )}
                  </div>

                  {/* Scope badges — read-only */}
                  {isActive && scopes.length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {scopes.map((scope) => (
                        <ScopeBadge key={scope} label={scopeLabel(tc, scope)} />
                      ))}
                    </div>
                  )}
                  {isActive && scopes.length === 0 && (
                    <p
                      style={{
                        fontSize: 13,
                        color: "var(--ink-faint)",
                        margin: 0,
                      }}
                    >
                      No scopes granted yet.
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* What's never shared note */}
      <div
        style={{
          marginTop: 28,
          background: "var(--surface-sunk)",
          border: "1px solid var(--line)",
          borderRadius: "var(--r-lg)",
          padding: "14px 18px",
        }}
      >
        <p
          style={{
            fontWeight: 600,
            fontSize: 13.5,
            color: "var(--ink-soft)",
            margin: "0 0 6px",
          }}
        >
          {tc.neverShared}
        </p>
        <p
          style={{
            fontSize: 13.5,
            color: "var(--ink-faint)",
            margin: 0,
            lineHeight: 1.55,
          }}
        >
          Children's details and identities never leave their device. Shared
          summaries are engagement only — not scores, assessments, or diagnostic
          data.
        </p>
      </div>

      {/* Confirm revoke-all modal */}
      {revokeTarget && (
        <RevokeConfirmModal
          email={revokeTarget.email}
          note={tc.immediateNote}
          cancelLabel={common.cancel}
          confirmLabel={tc.revokeAll}
          onCancel={() => setRevokeTarget(null)}
          onConfirm={confirmRevokeAll}
        />
      )}
    </div>
  );
}

// ---- Live (Supabase) consent management --------------------------------------
//
// Reads owned children + viewer links via the RLS-scoped adapter and drives the
// real grant/revoke/purge RPCs. People are shown as opaque refs (linkRef) and
// children as childRef — RLS deliberately withholds emails and child names.

interface OwnedRow {
  childId: string;
  viewers: { accountId: string; linkState: LinkState }[];
}
interface SharedRow {
  childId: string;
  ownerAccountId: string;
  linkState: LinkState;
  scope: ConsentScopeKey[];
}

function SharingConsentLive() {
  const { locale } = useComfort();
  const dict = getDictionary(locale);
  const tc = dict.service.consent;
  const common = dict.common;
  const ctx = useOutletContext<ServiceOutletContext | null>();
  const userId = ctx?.userId ?? "";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [owned, setOwned] = useState<OwnedRow[]>([]);
  const [shared, setShared] = useState<SharedRow[]>([]);
  const [consentStates, setConsentStates] = useState<Record<string, ConsentState>>({});
  const [revokeTarget, setRevokeTarget] = useState<{
    childId: string;
    viewerAccountId: string;
    email: string;
  } | null>(null);

  const reload = useCallback(async () => {
    const data = getPortalData();
    try {
      const [children, links, consents] = await Promise.all([
        data.children(),
        data.childLinks(),
        data.consents(),
      ]);
      const stateOf = (link: (typeof links)[number]): LinkState => {
        const c = link.consentId
          ? consents.find((x) => x.id === link.consentId)
          : undefined;
        if (!c) return "pending";
        if (c.revokedAt) return "revoked";
        return "active";
      };
      const ownedChildren = children.filter((c) => c.ownerAccountId === userId);
      const ownedRows: OwnedRow[] = ownedChildren.map((child) => ({
        childId: child.id,
        viewers: links
          .filter((l) => l.childId === child.id && l.relation === "viewer")
          .map((l) => ({ accountId: l.accountId, linkState: stateOf(l) })),
      }));
      const states: Record<string, ConsentState> = {};
      for (const child of ownedChildren) {
        const grants: Record<string, readonly ConsentScopeKey[]> = {};
        for (const l of links.filter(
          (x) => x.childId === child.id && x.relation === "viewer",
        )) {
          const c = l.consentId
            ? consents.find((x) => x.id === l.consentId)
            : undefined;
          if (c && !c.revokedAt && l.scope.length > 0) grants[l.accountId] = l.scope;
        }
        states[child.id] = { childId: child.id, ownerAccountId: userId, grants };
      }
      const sharedRows: SharedRow[] = links
        .filter((l) => l.accountId === userId && l.relation === "viewer")
        .map((l) => ({
          childId: l.childId,
          ownerAccountId:
            children.find((c) => c.id === l.childId)?.ownerAccountId ?? "",
          linkState: stateOf(l),
          scope: l.scope,
        }));
      setOwned(ownedRows);
      setShared(sharedRows);
      setConsentStates(states);
    } catch {
      setError("Couldn't load sharing right now. Please refresh.");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function handleToggle(
    childId: string,
    viewerAccountId: string,
    scope: ConsentScopeKey,
    checked: boolean,
  ) {
    setError(null);
    const state = consentStates[childId];
    if (!state) return;
    // Optimistic update, then persist via RPC and refetch (server truth wins).
    setConsentStates((prev) => {
      const s = prev[childId];
      if (!s) return prev;
      const next = checked
        ? grantScope(s, userId, viewerAccountId, scope)
        : revokeScope(s, userId, viewerAccountId, scope);
      return { ...prev, [childId]: next };
    });
    try {
      if (checked) {
        const held = state.grants[viewerAccountId] ?? [];
        const union = Array.from(new Set([...held, scope])) as ConsentScopeKey[];
        await getPortalData().grantConsent(childId, viewerAccountId, union);
      } else {
        await getPortalData().revokeConsent(childId, viewerAccountId, [scope]);
      }
      await reload();
    } catch {
      setError("That change didn't go through. Please try again.");
      await reload();
    }
  }

  async function confirmRevokeAll() {
    if (!revokeTarget) return;
    const target = revokeTarget;
    setRevokeTarget(null);
    try {
      await getPortalData().purgeSharedData(target.childId, target.viewerAccountId);
    } catch {
      setError("Couldn't remove access just now. Please try again.");
    }
    await reload();
  }

  if (loading) {
    return (
      <p style={{ color: "var(--ink-faint)", fontSize: 14 }}>Loading sharing…</p>
    );
  }

  const hasOwner = owned.length > 0;
  const hasShared = shared.length > 0;

  return (
    <div>
      <h1 style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 28, color: "var(--ink-deep)", margin: "0 0 8px" }}>
        {tc.title}
      </h1>
      <p style={{ color: "var(--ink-soft)", fontSize: 15.5, margin: "0 0 20px", lineHeight: 1.6 }}>
        You control what others can see about any child you own. Revoking takes
        effect immediately.
      </p>

      {error && (
        <div role="alert" style={{ background: "var(--peach-tint)", border: "1px solid var(--peach)", borderRadius: "var(--r-md)", padding: "12px 16px", color: "var(--peach-deep)", fontSize: 14.5, marginBottom: 20 }}>
          {error}
        </div>
      )}

      {!hasOwner && !hasShared && (
        <div style={{ background: "var(--surface)", border: "1px solid var(--line)", borderRadius: "var(--r-lg)", padding: "24px 22px" }}>
          <p style={{ color: "var(--ink-soft)", fontSize: 15.5, margin: 0 }}>
            No sharing set up yet. Link a child to get started.
          </p>
        </div>
      )}

      {hasOwner && (
        <section style={{ marginBottom: hasShared ? 40 : 0 }}>
          <SectionHeader label={tc.whatShared} />
          <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
            {owned.map(({ childId, viewers }) => (
              <div key={childId}>
                <h3 style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 18, color: "var(--ink-deep)", margin: "0 0 12px" }}>
                  {childRef(childId)}
                </h3>
                {viewers.length === 0 ? (
                  <p style={{ color: "var(--ink-soft)", fontSize: 15, background: "var(--surface)", border: "1px solid var(--line)", borderRadius: "var(--r-lg)", padding: "16px 20px", margin: 0 }}>
                    No adults are linked to this child yet.
                  </p>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {viewers.map((v) => {
                      const state = consentStates[childId];
                      if (!state) return null;
                      return (
                        <ViewerCard
                          key={v.accountId}
                          childId={childId}
                          accountId={v.accountId}
                          email={linkRef(v.accountId)}
                          effectiveLinkState={v.linkState}
                          consentState={state}
                          tc={tc}
                          onToggle={handleToggle}
                          onRevokeAll={(cid, aid, email) =>
                            setRevokeTarget({ childId: cid, viewerAccountId: aid, email })
                          }
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
          <p style={{ fontSize: 13, color: "var(--ink-faint)", marginTop: 18, lineHeight: 1.55 }}>
            {tc.immediateNote}
          </p>
        </section>
      )}

      {hasShared && (
        <section>
          <SectionHeader label="Children shared with you" />
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {shared.map((s) => (
              <div key={s.childId} style={{ background: s.linkState === "revoked" ? "var(--surface-sunk)" : s.linkState === "pending" ? "var(--peach-tint)" : "var(--surface)", border: `1px solid ${s.linkState === "pending" ? "var(--peach)" : "var(--line)"}`, borderRadius: "var(--r-lg)", padding: "16px 20px" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                  <div>
                    <span style={{ fontWeight: 600, fontSize: 15, color: "var(--ink-deep)", display: "block" }}>
                      {childRef(s.childId)}
                    </span>
                    <span style={{ fontSize: 13, color: "var(--ink-soft)" }}>
                      Shared by {linkRef(s.ownerAccountId)}
                    </span>
                  </div>
                  {s.linkState === "pending" && (
                    <span style={{ fontSize: 12.5, color: "var(--peach-deep)", fontWeight: 600 }}>Awaiting consent</span>
                  )}
                  {s.linkState === "revoked" && (
                    <span style={{ fontSize: 12.5, color: "var(--ink-faint)", fontWeight: 600 }}>Access removed</span>
                  )}
                </div>
                {s.linkState === "active" && s.scope.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 12 }}>
                    {s.scope.map((scope) => (
                      <ScopeBadge key={scope} label={scopeLabel(tc, scope)} />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      <div style={{ marginTop: 28, background: "var(--surface-sunk)", border: "1px solid var(--line)", borderRadius: "var(--r-lg)", padding: "14px 18px" }}>
        <p style={{ fontWeight: 600, fontSize: 13.5, color: "var(--ink-soft)", margin: "0 0 6px" }}>
          {tc.neverShared}
        </p>
        <p style={{ fontSize: 13.5, color: "var(--ink-faint)", margin: 0, lineHeight: 1.55 }}>
          Children's details and identities never leave their device. Shared
          summaries are engagement only — not scores, assessments, or diagnostic
          data.
        </p>
      </div>

      {revokeTarget && (
        <RevokeConfirmModal
          email={revokeTarget.email}
          note={tc.immediateNote}
          cancelLabel={common.cancel}
          confirmLabel={tc.revokeAll}
          onCancel={() => setRevokeTarget(null)}
          onConfirm={confirmRevokeAll}
        />
      )}
    </div>
  );
}
